import axios, { type AxiosInstance, type AxiosError } from 'axios'
import type {
  User,
  LoginCredentials,
  TokenResponse,
  Timesheet,
  TimesheetListItem,
  TimesheetCreate,
  DayEntry,
  DayEntryCreate,
  Project,
  ProjectCreate,
  ProjectUpdate,
  Holiday,
  OvertimeBalanceResponse,
  OvertimeAccountResponse,
  OvertimeAdjustmentCreate,
  OvertimeAdjustmentResponse,
  OvertimeAccountWithAdjustmentsResponse,
  VacationBalanceResponse,
  YearlyBalanceResponse,
  YearlyBalanceCreate,
  YearlyBalanceUpdate,
  CarryoverExpiryOverrideUpdate,
  EmployeeBalanceSummary,
  BalanceCorrectionCreate,
  BalanceCorrectionResponse,
  Task,
  FavoriteColumn,
  TimesheetReminderStatus,
  TimesheetReminderPreview,
  TimesheetReminderResult,
} from '@/types'

const API_URL = import.meta.env.VITE_API_URL || '/api'

class ApiService {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('hermes_token')
          window.location.href = '/login'
        }
        return Promise.reject(error)
      }
    )
  }

  setToken(token: string) {
    this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`
  }

  clearToken() {
    delete this.client.defaults.headers.common['Authorization']
  }

  // Auth
  async login(credentials: LoginCredentials): Promise<TokenResponse> {
    const formData = new FormData()
    formData.append('username', credentials.username)
    formData.append('password', credentials.password)
    const response = await this.client.post<TokenResponse>('/auth/token', formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    })
    return response.data
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.client.get<User>('/auth/me')
    return response.data
  }

  // Timesheets
  async getTimesheets(ownOnly: boolean = false): Promise<Timesheet[]> {
    const response = await this.client.get<Timesheet[]>('/timesheets/', {
      params: { own_only: ownOnly }
    })
    return response.data
  }

  // Lightweight list endpoint for faster loading of large datasets
  async getTimesheetsList(ownOnly: boolean = false): Promise<TimesheetListItem[]> {
    const response = await this.client.get<TimesheetListItem[]>('/timesheets/list', {
      params: { own_only: ownOnly }
    })
    return response.data
  }

  async getTimesheetCounts(): Promise<{
    pending: number
    approved: number
    released: number
    inprogress: number
    rejected: number
    total: number
  }> {
    const response = await this.client.get('/timesheets/count')
    return response.data
  }

  async getRecentTimesheets(limit: number = 10): Promise<TimesheetListItem[]> {
    const response = await this.client.get<TimesheetListItem[]>('/timesheets/recent', {
      params: { limit }
    })
    return response.data
  }

  async getTimesheet(id: number): Promise<Timesheet> {
    const response = await this.client.get<Timesheet>(`/timesheets/${id}`)
    return response.data
  }

  async createTimesheet(data: TimesheetCreate): Promise<Timesheet> {
    const response = await this.client.post<Timesheet>('/timesheets/', data)
    return response.data
  }

  async deleteTimesheet(id: number): Promise<void> {
    await this.client.delete(`/timesheets/${id}`)
  }

  async addDayEntry(timesheetId: number, entry: DayEntryCreate): Promise<DayEntry> {
    const response = await this.client.post<DayEntry>(
      `/timesheets/${timesheetId}/entries`,
      entry
    )
    return response.data
  }

  async updateDayEntry(
    timesheetId: number,
    entryDate: string,
    entry: DayEntryCreate
  ): Promise<DayEntry> {
    const response = await this.client.put<DayEntry>(
      `/timesheets/${timesheetId}/entries/${entryDate}`,
      entry
    )
    return response.data
  }

  async deleteDayEntry(timesheetId: number, entryDate: string): Promise<void> {
    await this.client.delete(`/timesheets/${timesheetId}/entries/${entryDate}`)
  }

  async submitTimesheet(id: number): Promise<Timesheet> {
    const response = await this.client.post<Timesheet>(`/timesheets/${id}/submit`)
    return response.data
  }

  async approveTimesheet(id: number): Promise<Timesheet> {
    const response = await this.client.post<Timesheet>(`/timesheets/${id}/approve`)
    return response.data
  }

  async releaseTimesheet(id: number): Promise<Timesheet> {
    const response = await this.client.post<Timesheet>(`/timesheets/${id}/release`)
    return response.data
  }

  async rejectTimesheet(id: number, reason: string): Promise<Timesheet> {
    const response = await this.client.post<Timesheet>(`/timesheets/${id}/reject`, {
      reason,
    })
    return response.data
  }

  // For approval queue
  async getPendingTimesheets(): Promise<Timesheet[]> {
    const response = await this.client.get<Timesheet[]>('/timesheets/pending')
    return response.data
  }

  // Projects
  async getProjects(activeOnly: boolean = true): Promise<Project[]> {
    const response = await this.client.get<Project[]>('/projects/', {
      params: { active_only: activeOnly }
    })
    return response.data
  }

  async getProject(id: number): Promise<Project> {
    const response = await this.client.get<Project>(`/projects/${id}`)
    return response.data
  }

  async createProject(data: ProjectCreate): Promise<Project> {
    const response = await this.client.post<Project>('/projects/', data)
    return response.data
  }

  async updateProject(id: number, data: ProjectUpdate): Promise<Project> {
    const response = await this.client.patch<Project>(`/projects/${id}`, data)
    return response.data
  }

  // Project Tasks
  async getProjectTasks(projectId: number, activeOnly: boolean = true): Promise<Task[]> {
    const response = await this.client.get<Task[]>(`/projects/${projectId}/tasks`, {
      params: { active_only: activeOnly }
    })
    return response.data
  }

  async createTask(projectId: number, data: { name: string; description?: string; max_hours?: number }): Promise<Task> {
    const response = await this.client.post<Task>(`/projects/${projectId}/tasks`, data)
    return response.data
  }

  async updateTask(projectId: number, taskId: number, data: { name?: string; description?: string; max_hours?: number; active?: boolean }): Promise<Task> {
    const response = await this.client.patch<Task>(`/projects/${projectId}/tasks/${taskId}`, data)
    return response.data
  }

  async getMyProjectsHoursSummary(year?: number, month?: number): Promise<any[]> {
    const response = await this.client.get('/projects/my-projects/hours-summary', {
      params: { year, month }
    })
    return response.data
  }

  async getProjectHoursDetails(projectId: number, year?: number, month?: number): Promise<any> {
    const response = await this.client.get(`/projects/${projectId}/hours`, {
      params: { year, month }
    })
    return response.data
  }

  // Employee Favorite Projects (columns)
  async getFavoriteColumns(): Promise<FavoriteColumn[]> {
    const response = await this.client.get<FavoriteColumn[]>('/employees/me/projects')
    return response.data
  }

  async addFavoriteColumn(projectId: number, taskId?: number | null): Promise<void> {
    await this.client.post(`/employees/me/projects/${projectId}`, null, {
      params: taskId != null ? { task_id: taskId } : undefined,
    })
  }

  async removeFavoriteColumn(projectId: number, year: number, month: number, taskId?: number | null): Promise<void> {
    const params: Record<string, unknown> = { year, month }
    if (taskId != null) params.task_id = taskId
    await this.client.delete(`/employees/me/projects/${projectId}`, { params })
  }

  // Holidays
  async getHolidays(year: number, region: string): Promise<Holiday[]> {
    const response = await this.client.get<Holiday[]>('/admin/holidays', {
      params: { year, region },
    })
    return response.data
  }

  async syncHolidays(year: number): Promise<void> {
    await this.client.post('/admin/holidays/sync', null, { params: { year } })
  }

  // Admin - Test Email
  async sendTestEmail(toEmail: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post<{ success: boolean; message: string }>('/admin/test-email', {
      to_email: toEmail,
    })
    return response.data
  }

  // Admin - Timesheet Reminders
  async getTimesheetReminderStatus(year: number, month: number): Promise<TimesheetReminderStatus> {
    const response = await this.client.get<TimesheetReminderStatus>('/admin/timesheet-reminders/status', {
      params: { year, month },
    })
    return response.data
  }

  async previewTimesheetReminders(year: number, month: number): Promise<TimesheetReminderPreview> {
    const response = await this.client.post<TimesheetReminderPreview>('/admin/timesheet-reminders/preview', {
      year, month,
    })
    return response.data
  }

  async sendTimesheetReminders(year: number, month: number, customText?: string, employeeIds?: number[]): Promise<TimesheetReminderResult> {
    const response = await this.client.post<TimesheetReminderResult>('/admin/timesheet-reminders/send', {
      year, month, custom_text: customText || null, employee_ids: employeeIds || null,
    })
    return response.data
  }

  // Reports
  async downloadTimesheetPdf(id: number, includeVacation = false): Promise<Blob> {
    const endpoint = includeVacation
      ? `/reports/timesheet/${id}/pdf?type=vacation`
      : `/reports/timesheet/${id}/pdf`
    const response = await this.client.get(endpoint, { responseType: 'blob' })
    return response.data
  }

  async getMonthlySummary(year: number, month: number): Promise<any> {
    const response = await this.client.get(`/reports/summary/${year}/${month}`)
    return response.data
  }

  async downloadMonthlyCombinedPdf(year: number, month: number, includeVacation = false, includeRejected = false, sbReport = false): Promise<Blob> {
    const params = new URLSearchParams()
    if (includeVacation) params.append('include_vacation', 'true')
    if (includeRejected) params.append('include_rejected', 'true')
    if (sbReport) params.append('sb_report', 'true')
    const queryString = params.toString() ? `?${params.toString()}` : ''
    const response = await this.client.get(`/reports/monthly/${year}/${month}/pdf${queryString}`, {
      responseType: 'blob',
    })
    return response.data
  }

  async downloadDatevExport(year: number, month: number): Promise<{ blob: Blob; filename: string }> {
    const response = await this.client.get(`/reports/datev/${year}/${month}`, {
      responseType: 'blob',
    })
    const disposition = response.headers['content-disposition'] || ''
    const match = disposition.match(/filename=([^;\s]+)/)
    const filename = match ? match[1] : `datev_${year}-${String(month).padStart(2, '0')}.zip`
    return { blob: response.data, filename }
  }

  async sendMonthlyReportEmail(
    year: number,
    month: number,
    template: 'vr' | 'sb',
    includeVacation = false,
    includeRejected = false,
    customText?: string,
    customTo?: string[],
    customCc?: string[]
  ): Promise<{ success: boolean; message: string }> {
    const params = new URLSearchParams()
    params.append('template', template)
    if (includeVacation) params.append('include_vacation', 'true')
    if (includeRejected) params.append('include_rejected', 'true')
    const body: Record<string, any> = {}
    if (customText !== undefined) body.custom_text = customText
    if (customTo !== undefined) body.custom_to = customTo
    if (customCc !== undefined) body.custom_cc = customCc
    const response = await this.client.post(`/reports/monthly/${year}/${month}/send?${params.toString()}`, Object.keys(body).length ? body : undefined)
    return response.data
  }

  async sendProjectsReportEmail(
    year: number,
    month: number,
    includeRejected = false,
    customText?: string,
    customTo?: string[],
    customCc?: string[]
  ): Promise<{ success: boolean; message: string }> {
    const params = new URLSearchParams()
    params.append('template', 'projects')
    if (includeRejected) params.append('include_rejected', 'true')
    const body: Record<string, any> = {}
    if (customText !== undefined) body.custom_text = customText
    if (customTo !== undefined) body.custom_to = customTo
    if (customCc !== undefined) body.custom_cc = customCc
    const response = await this.client.post(`/reports/projects/${year}/${month}/send?${params.toString()}`, Object.keys(body).length ? body : undefined)
    return response.data
  }

  async getEmailPreview(
    template: string,
    year: number,
    month: number
  ): Promise<{ success: boolean; to: string[]; cc: string[]; subject: string; text: string }> {
    const response = await this.client.get(`/reports/email-preview/${template}?year=${year}&month=${month}`)
    return response.data
  }

  async getProjectsMonthlySummary(year: number, month: number, statuses: string[] = ['RELEASED']): Promise<any> {
    const params = new URLSearchParams()
    statuses.forEach(s => params.append('statuses', s))
    const response = await this.client.get(`/reports/projects/summary/${year}/${month}?${params.toString()}`)
    return response.data
  }

  async downloadAllProjectsPdf(year: number, month: number, statuses: string[] = ['RELEASED']): Promise<Blob> {
    const params = new URLSearchParams()
    statuses.forEach(s => params.append('statuses', s))
    const response = await this.client.get(`/reports/projects/${year}/${month}/zip?${params.toString()}`, {
      responseType: 'blob',
    })
    return response.data
  }

  async getProjectsYearlyData(year: number): Promise<any> {
    const response = await this.client.get(`/reports/projects/yearly/${year}`)
    return response.data
  }

  async getAvailableYears(): Promise<{ years: number[] }> {
    const response = await this.client.get(`/reports/available-years`)
    return response.data
  }

  async getProjectReport(
    projectId: number,
    year: number,
    month: number
  ): Promise<any> {
    const response = await this.client.get(`/reports/project/${projectId}/${year}/${month}`)
    return response.data
  }

  async downloadProjectReport(
    projectId: number,
    year: number,
    month: number,
    statuses: string[] = ['RELEASED']
  ): Promise<Blob> {
    const params = new URLSearchParams()
    statuses.forEach(s => params.append('statuses', s))
    const response = await this.client.get(`/reports/project/${projectId}/${year}/${month}/pdf?${params.toString()}`, {
      responseType: 'blob',
    })
    return response.data
  }

  async downloadProjectOdsExport(projectId: number, year: number, month: number): Promise<Blob> {
    const response = await this.client.get(`/reports/project/${projectId}/${year}/${month}/ods`, {
      responseType: 'blob',
    })
    return response.data
  }

  // Employees (admin)
  async getEmployees(activeOnly: boolean = true): Promise<User[]> {
    const response = await this.client.get<User[]>('/employees/', {
      params: { active_only: activeOnly }
    })
    return response.data
  }

  async updateEmployee(employeeId: number, data: Partial<User>): Promise<User> {
    const response = await this.client.patch<User>(`/employees/${employeeId}`, data)
    return response.data
  }

  async createEmployee(data: Partial<User> & { password: string }): Promise<User> {
    const response = await this.client.post<User>('/employees/', data)
    return response.data
  }

  async changeMyPassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    const response = await this.client.post<{ message: string }>('/employees/me/password', {
      current_password: currentPassword,
      new_password: newPassword
    })
    return response.data
  }

  async setEmployeePassword(employeeId: number, newPassword: string): Promise<{ message: string }> {
    const response = await this.client.put<{ message: string }>(`/employees/${employeeId}/password`, {
      new_password: newPassword
    })
    return response.data
  }

  // Overtime
  async getMyOvertimeBalance(year?: number, month?: number): Promise<OvertimeBalanceResponse> {
    const params: Record<string, number> = {}
    if (year) params.year = year
    if (month) params.month = month
    const response = await this.client.get<OvertimeBalanceResponse>('/overtime/me', { params })
    return response.data
  }

  async getEmployeeOvertimeBalance(employeeId: number, year?: number, month?: number): Promise<OvertimeBalanceResponse> {
    const params: Record<string, number> = {}
    if (year) params.year = year
    if (month) params.month = month
    const response = await this.client.get<OvertimeBalanceResponse>(`/overtime/${employeeId}`, { params })
    return response.data
  }

  async getMyOvertimeHistory(limit: number = 12): Promise<OvertimeAccountResponse[]> {
    const response = await this.client.get<OvertimeAccountResponse[]>('/overtime/me/history', {
      params: { limit }
    })
    return response.data
  }

  async getOvertimeAccountDetails(
    employeeId: number,
    year: number,
    month: number
  ): Promise<OvertimeAccountWithAdjustmentsResponse> {
    const response = await this.client.get<OvertimeAccountWithAdjustmentsResponse>(
      `/overtime/${employeeId}/${year}/${month}`
    )
    return response.data
  }

  async createOvertimeAdjustment(
    employeeId: number,
    year: number,
    month: number,
    adjustment: OvertimeAdjustmentCreate
  ): Promise<OvertimeAccountWithAdjustmentsResponse> {
    const response = await this.client.post<OvertimeAccountWithAdjustmentsResponse>(
      `/overtime/${employeeId}/${year}/${month}/adjustment`,
      adjustment
    )
    return response.data
  }

  async getOvertimeAdjustments(
    employeeId: number,
    year: number,
    month: number
  ): Promise<OvertimeAdjustmentResponse[]> {
    const response = await this.client.get<OvertimeAdjustmentResponse[]>(
      `/overtime/${employeeId}/${year}/${month}/adjustments`
    )
    return response.data
  }

  // Vacation
  async getMyVacationBalance(year?: number): Promise<VacationBalanceResponse> {
    const params: Record<string, number> = {}
    if (year) params.year = year
    const response = await this.client.get<VacationBalanceResponse>('/vacation/me/balance', { params })
    return response.data
  }

  async getMyBalanceSummary(year?: number): Promise<EmployeeBalanceSummary> {
    const params: Record<string, number> = {}
    if (year) params.year = year
    const response = await this.client.get<EmployeeBalanceSummary>('/vacation/me/summary', { params })
    return response.data
  }

  async getEmployeeVacationBalance(employeeId: number, year?: number): Promise<VacationBalanceResponse> {
    const params: Record<string, number> = {}
    if (year) params.year = year
    const response = await this.client.get<VacationBalanceResponse>(`/vacation/${employeeId}/balance`, { params })
    return response.data
  }

  async getEmployeeBalanceSummary(employeeId: number, year?: number): Promise<EmployeeBalanceSummary> {
    const params: Record<string, number> = {}
    if (year) params.year = year
    const response = await this.client.get<EmployeeBalanceSummary>(`/vacation/${employeeId}/summary`, { params })
    return response.data
  }

  async getEmployeeYearlyBalances(employeeId: number): Promise<YearlyBalanceResponse[]> {
    const response = await this.client.get<YearlyBalanceResponse[]>(`/vacation/${employeeId}/yearly-balances`)
    return response.data
  }

  async setEmployeeYearlyBalance(employeeId: number, data: YearlyBalanceCreate): Promise<YearlyBalanceResponse> {
    const response = await this.client.post<YearlyBalanceResponse>(`/vacation/${employeeId}/yearly-balance`, data)
    return response.data
  }

  async updateEmployeeYearlyBalance(
    employeeId: number,
    year: number,
    data: YearlyBalanceUpdate
  ): Promise<YearlyBalanceResponse> {
    const response = await this.client.put<YearlyBalanceResponse>(
      `/vacation/${employeeId}/yearly-balance/${year}`,
      data
    )
    return response.data
  }

  async updateEmployeeCarryoverExpiry(
    employeeId: number,
    year: number,
    data: CarryoverExpiryOverrideUpdate
  ): Promise<VacationBalanceResponse> {
    const response = await this.client.put<VacationBalanceResponse>(
      `/vacation/${employeeId}/carryover-expiry/${year}`,
      data
    )
    return response.data
  }

  // === Balance Corrections ===
  
  async getMyCorrections(type?: string, year?: number): Promise<BalanceCorrectionResponse[]> {
    const params = new URLSearchParams()
    if (type) params.append('correction_type', type)
    if (year) params.append('year', year.toString())
    const response = await this.client.get<BalanceCorrectionResponse[]>(`/vacation/me/corrections?${params}`)
    return response.data
  }

  async getEmployeeCorrections(
    employeeId: number,
    type?: string,
    year?: number
  ): Promise<BalanceCorrectionResponse[]> {
    const params = new URLSearchParams()
    if (type) params.append('correction_type', type)
    if (year) params.append('year', year.toString())
    const response = await this.client.get<BalanceCorrectionResponse[]>(
      `/vacation/${employeeId}/corrections?${params}`
    )
    return response.data
  }

  async createBalanceCorrection(
    employeeId: number,
    data: BalanceCorrectionCreate
  ): Promise<BalanceCorrectionResponse> {
    const response = await this.client.post<BalanceCorrectionResponse>(
      `/vacation/${employeeId}/corrections`,
      data
    )
    return response.data
  }

  // ──── AI Text Improvement ────

  async getAiStatus(): Promise<boolean> {
    try {
      const response = await this.client.get<{ enabled: boolean }>('/ai/status')
      return response.data.enabled
    } catch {
      return false
    }
  }

  async improveText(text: string): Promise<string> {
    const response = await this.client.post<{ improved: string }>('/ai/improve-text', { text })
    return response.data.improved
  }

  async getLastDescription(projectId: number, beforeDate?: string): Promise<string> {
    try {
      const params: Record<string, any> = { project_id: projectId }
      if (beforeDate) params.before_date = beforeDate
      const response = await this.client.get<{ description: string }>('/ai/last-description', {
        params
      })
      return response.data.description || ''
    } catch {
      return ''
    }
  }

  // === Settings ===

  async getAppSettings(): Promise<Array<{ key: string; value: string; description: string | null }>> {
    const response = await this.client.get('/settings')
    return response.data
  }

  async updateAppSetting(key: string, value: string): Promise<{ key: string; value: string; description: string | null }> {
    const response = await this.client.put(`/settings/${key}`, { value })
    return response.data
  }

  async updateCarryoverExpiry(month: number, day: number, applyToExisting: boolean): Promise<{ updated_accounts: number }> {
    const response = await this.client.put('/settings/carryover-expiry', {
      month,
      day,
      apply_to_existing: applyToExisting,
    })
    return response.data
  }

  // Generic methods for direct access
  get<T = any>(url: string, config?: any) {
    return this.client.get<T>(url, config)
  }
  
  post<T = any>(url: string, data?: any, config?: any) {
    return this.client.post<T>(url, data, config)
  }
  
  patch<T = any>(url: string, data?: any, config?: any) {
    return this.client.patch<T>(url, data, config)
  }
  
  put<T = any>(url: string, data?: any, config?: any) {
    return this.client.put<T>(url, data, config)
  }
  
  remove<T = any>(url: string, config?: any) {
    return this.client.delete<T>(url, config)
  }
}

export const api = new ApiService()
export default api
